import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="container py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-heading text-4xl font-bold text-primary-700 mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-text-muted">
              Last updated: {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>

          {/* Content */}
          <div className="card p-8 space-y-8">
            <section>
              <h2 className="font-heading text-2xl font-bold text-primary-700 mb-4">
                1. Introduction
              </h2>
              <p className="text-text-muted leading-relaxed mb-4">
                Dr. Adesh Palyekar ("we," "our," or "us") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                when you visit our website or contact us for medical consultations.
              </p>
              <p className="text-text-muted leading-relaxed">
                This policy applies to information collected through our website, contact forms, 
                and any other interactions you may have with our medical practice.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold text-primary-700 mb-4">
                2. Information We Collect
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-heading text-xl font-semibold text-primary-600 mb-2">
                    Personal Information
                  </h3>
                  <p className="text-text-muted leading-relaxed">
                    We may collect personal information that you voluntarily provide to us, including:
                  </p>
                  <ul className="list-disc list-inside text-text-muted mt-2 space-y-1">
                    <li>Name and contact information (phone, email)</li>
                    <li>Medical history and health information</li>
                    <li>Appointment requests and consultation details</li>
                    <li>Communication preferences</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-heading text-xl font-semibold text-primary-600 mb-2">
                    Automatically Collected Information
                  </h3>
                  <p className="text-text-muted leading-relaxed">
                    When you visit our website, we automatically collect certain information, including:
                  </p>
                  <ul className="list-disc list-inside text-text-muted mt-2 space-y-1">
                    <li>IP address and device information</li>
                    <li>Browser type and version</li>
                    <li>Pages visited and time spent on site</li>
                    <li>Referring website information</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold text-primary-700 mb-4">
                3. How We Use Your Information
              </h2>
              <p className="text-text-muted leading-relaxed mb-4">
                We use the information we collect for the following purposes:
              </p>
              <ul className="list-disc list-inside text-text-muted space-y-2">
                <li>Providing medical consultations and services</li>
                <li>Responding to your inquiries and appointment requests</li>
                <li>Improving our website and services</li>
                <li>Sending important medical updates and information</li>
                <li>Complying with legal and regulatory requirements</li>
                <li>Ensuring the security and integrity of our services</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold text-primary-700 mb-4">
                4. Information Sharing and Disclosure
              </h2>
              <p className="text-text-muted leading-relaxed mb-4">
                We do not sell, trade, or otherwise transfer your personal information to third parties 
                without your consent, except in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-text-muted space-y-2">
                <li>With your explicit consent for medical referrals or consultations</li>
                <li>To comply with legal obligations or court orders</li>
                <li>To protect our rights, property, or safety</li>
                <li>In emergency medical situations where disclosure is necessary</li>
                <li>To trusted service providers who assist in our operations (under strict confidentiality agreements)</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold text-primary-700 mb-4">
                5. Data Security
              </h2>
              <p className="text-text-muted leading-relaxed mb-4">
                We implement appropriate technical and organizational measures to protect your personal information, including:
              </p>
              <ul className="list-disc list-inside text-text-muted space-y-2">
                <li>Encryption of sensitive data in transit and at rest</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and authentication measures</li>
                <li>Staff training on data protection and privacy</li>
                <li>Secure hosting and infrastructure</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold text-primary-700 mb-4">
                6. Your Rights
              </h2>
              <p className="text-text-muted leading-relaxed mb-4">
                You have the following rights regarding your personal information:
              </p>
              <ul className="list-disc list-inside text-text-muted space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
                <li><strong>Portability:</strong> Request transfer of your data to another provider</li>
                <li><strong>Objection:</strong> Object to processing of your personal information</li>
                <li><strong>Withdrawal:</strong> Withdraw consent for data processing</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold text-primary-700 mb-4">
                7. Medical Information and HIPAA
              </h2>
              <p className="text-text-muted leading-relaxed mb-4">
                As a medical practice, we are committed to protecting your health information in accordance 
                with applicable medical privacy laws and regulations. Any medical information you provide 
                is treated with the highest level of confidentiality and security.
              </p>
              <p className="text-text-muted leading-relaxed">
                We maintain appropriate safeguards to protect your medical information and ensure compliance 
                with relevant healthcare privacy regulations.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-bold text-primary-700 mb-4">
                8. Contact Information
              </h2>
              <p className="text-text-muted leading-relaxed mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-primary-50 rounded-lg p-6">
                <div className="space-y-2">
                  <p className="text-text-muted">
                    <strong>Email:</strong> contact@adeshpalyekar.com
                  </p>
                  <p className="text-text-muted">
                    <strong>Phone:</strong> +91 8080485895
                  </p>
                  <p className="text-text-muted">
                    <strong>Address:</strong> Konkan Cancer and Multispeciality Hospital, Kudal, Maharashtra
                  </p>
                </div>
              </div>
            </section>

            {/* Back to Home */}
            <div className="text-center pt-8">
              <Link 
                to="/" 
                className="btn-primary inline-flex items-center gap-2"
              >
                <i className="fas fa-arrow-left"></i>
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 